import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import { withStyles } from '@mui/styles';
import Downshift from 'downshift';

const styles = (theme) => ({
  root: {
    flexGrow: 1,
  },
  container: {
    flexGrow: 1,
    position: 'relative',
  },
  paper: {
    position: 'absolute',
    zIndex: 20,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  inputRoot: {
    flexWrap: 'wrap',
  },
  inputInput: {
    width: 'auto',
    flexGrow: 1,
  },
});

function getSuggestions(suggestions, value, { showEmpty = false } = {}) {
  const inputValue = value.trim().toLowerCase();
  const inputLength = inputValue.length;
  let count = 0;

  return inputLength === 0 && !showEmpty
    ? []
    : suggestions.filter((suggestion) => {
      const keep = count < 5 && suggestion.label.toLowerCase().indexOf(inputValue) !== -1;
      if (keep) {
        count += 1;
      }

      return keep;
    });
}

function renderSuggestion(suggestionProps) {
  const {
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
  } = suggestionProps;
  const isHighlighted = highlightedIndex === index;
  const isSelected = ((selectedItem ? selectedItem.label : '') || '').indexOf(suggestion.label) > -1;

  return (
    <MenuItem
      {...itemProps}
      key={suggestion.label}
      selected={isHighlighted}
      component='div'
      style={{
        fontWeight: isSelected ? 500 : 400,
      }}
    >
      {suggestion.label}
    </MenuItem>
  );
}

renderSuggestion.propTypes = {
  highlightedIndex: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.number]).isRequired,
  index: PropTypes.number.isRequired,
  itemProps: PropTypes.object.isRequired,
  selectedItem: PropTypes.string.isRequired,
  suggestion: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
};

function renderInput(inputProps) {
  const {
    InputProps,
    classes,
    ref,
    ...other
  } = inputProps;

  return (
    <TextField
      InputProps={{
        inputRef: ref,
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput,
        },
        ...InputProps,
      }}
      {...other}
    />
  );
}

function AutocompleteSingleWidget(props) {
  const {
    schema,
    value,
    onChange,
    classes,
  } = props;

  const initialItem = schema.suggestions.filter((s) => s.value === value)[0];

  return (
    <div className={classes.root}>
      <Downshift
        initialSelectedItem={initialItem}
        itemToString={(item) => (item ? item.label : '')}
        onChange={(selectedItem) => { if (selectedItem) onChange(selectedItem.value); }}
      >
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          highlightedIndex,
          inputValue,
          isOpen,
          selectedItem,
        }) => {
          const {
            onBlur: downshiftOnBlur,
            onFocus: downshiftOnFocus,
            ...inputProps
          } = getInputProps({
            placeholder: `Search for a ${schema.title}`,
          });

          return (
            <div className={classes.container}>
              {renderInput({
                fullWidth: true,
                classes,
                label: schema.title,
                InputLabelProps: getLabelProps({ shrink: true }),
                InputProps: { onBlur: downshiftOnBlur, onFocus: downshiftOnFocus },
                inputProps,
              })}
              <div {...getMenuProps()}>
                {isOpen ? (
                  <Paper className={classes.paper} square>
                    {getSuggestions(schema.suggestions, inputValue).map(
                      (suggestion, index) => renderSuggestion({
                        suggestion,
                        index,
                        itemProps: getItemProps({ item: suggestion }),
                        highlightedIndex,
                        selectedItem,
                      }),
                    )}
                  </Paper>
                ) : null}
              </div>
            </div>
          );
        }}
      </Downshift>
    </div>
  );
}

AutocompleteSingleWidget.defaultProps = {
  autofocus: false,
};

AutocompleteSingleWidget.propTypes = {
  schema: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  options: PropTypes.shape({
    suggestions: PropTypes.array,
  }).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  multiple: PropTypes.bool,
  autofocus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  rawErrors: PropTypes.array,
  classes: PropTypes.object.isRequired,
};

export { AutocompleteSingleWidget }; // unwrapped for RJSF v6
const styled = withStyles(styles)(AutocompleteSingleWidget);
export { styled as StyledAutocompleteSingleWidget };
export default styled;
